import {
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type EventMessage,
} from '@azure/msal-browser';
import { DEV_BYPASS_AUTH, IS_DEV_SERVER_FLAG } from './devFlags';
import { hasAppAccessGroup } from './utils/roles';

/**
 * Azure AD B2C sign-in, mirroring AdminPortal.UI/src/authConfig.ts.
 *
 * This replaced the legacy corporate-AAD SSO. Every environment now reaches the
 * backend through APIM in front of DataNovaProxy, which validates the B2C JWT
 * against the tenant's JWKS (audience = the B2C client id), checks the role/path
 * matrix, and injects the connector's own service token server-side. So there is
 * nothing for the browser to exchange and no backend credential it can hold: the
 * only thing sent is `Bearer <id token>`.
 *
 * The connector-token exchange this module used to support (`/vendors/checklogin`,
 * whose token was cached in sessionStorage and sent bare) is gone, along with the
 * `VITE_CHECK_LOGIN_ENDPOINT` that selected it. Local development points at the
 * dev APIM, so no mode calls a connector directly any more.
 *
 * The token sent is the **id token**, not the access token: that is what the
 * proxy validates and what carries the `roles` claim its matrix reads, and it is
 * what MicroUI.CMS sends today (`accessTokenHook.ts` returns `response.idToken`).
 * The `admin_portal` scope is still requested so MSAL has something to refresh.
 */

const tenant = import.meta.env.VITE_B2C_TENANT;
const policy = import.meta.env.VITE_B2C_POLICY;
const clientId = import.meta.env.VITE_B2C_CLIENT_ID;

export const b2cPolicies = {
  names: {
    signIn: policy,
  },
  authorities: {
    signIn: {
      authority: `https://${tenant}.b2clogin.com/${tenant}.onmicrosoft.com/${policy}`,
    },
  },
  authorityDomain: `${tenant}.b2clogin.com`,
};

export const loginRequest = {
  scopes: [`https://${tenant}.onmicrosoft.com/${clientId}/admin_portal`],
};

/**
 * Redirect URI to hand MSAL: whichever origin is actually serving the app.
 *
 * This app is reachable on more than one hostname - its own
 * `*.azurewebsites.net` and the Front Door endpoint in front of it (and a custom
 * domain later) - so a hardcoded value is wrong for every host except the one it
 * names. B2C honours whatever `redirect_uri` it is sent, so hardcoding the
 * App Service host meant signing in via Front Door bounced the user back out to
 * the App Service host afterwards.
 *
 * Using the live origin also covers the dev server, where the env file's value
 * (a deployed hostname) would either fail B2C's reply-URL check or send the
 * developer to the deployed site. `strictPort` pins that origin to
 * `http://localhost:3000`, already a registered reply URL.
 *
 * Every origin the app is served from must be registered as a reply URL on the
 * B2C app registration. `VITE_REDIRECT_URI` is now only the non-browser
 * fallback, and records the canonical host for the environment.
 */
const resolveRedirectUri = (): string =>
  typeof window !== 'undefined'
    ? window.location.origin
    : import.meta.env.VITE_REDIRECT_URI;

export const msalConfig = {
  auth: {
    clientId,
    authority: b2cPolicies.authorities.signIn.authority,
    // B2C authorities are not trusted by MSAL by default; without this the
    // authority is rejected before any redirect happens.
    knownAuthorities: [b2cPolicies.authorityDomain],
    redirectUri: resolveRedirectUri(),
    postLogoutRedirectUri: '/',
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage' as const,
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Set when `handleRedirectPromise()` rejects — e.g. B2C came back with
 * `error=access_denied`, or the `state` parameter no longer matches what this
 * browser session expects. `establishSession` surfaces it instead of quietly
 * falling back to `signedOut`.
 *
 * A module-level variable, not a return value, because `bootstrap()` in
 * `main.tsx` cannot render anything — not even the error boundary, which only
 * catches errors during React's render phase — until `initializeMsal()`
 * settles. An uncaught rejection here previously killed that `await` outright:
 * the page stayed blank with nothing but a console error, which is
 * indistinguishable from "did nothing" to whoever is looking at it.
 */
let redirectError: string | null = null;

/**
 * Initializes MSAL and processes any pending redirect response.
 *
 * Must be awaited before rendering: MSAL v3 requires `initialize()` and
 * `handleRedirectPromise()` to settle first, or the redirect response is dropped
 * and sign-in loops.
 *
 * The account-tracking callback is registered *before* `handleRedirectPromise()`
 * because that call is what emits `LOGIN_SUCCESS` — registering afterwards misses
 * the event entirely. The sign-in result is also returned directly rather than
 * being picked up from the event, so nothing here depends on event ordering.
 */
export const initializeMsal =
  async (): Promise<AuthenticationResult | null> => {
    await msalInstance.initialize();

    msalInstance.addEventCallback((event: EventMessage) => {
      const isSignInEvent =
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.SSO_SILENT_SUCCESS;

      const account = (event.payload as AuthenticationResult)?.account;
      if (isSignInEvent && account) {
        msalInstance.setActiveAccount(account);
      }
    });

    let result: AuthenticationResult | null = null;
    try {
      result = await msalInstance.handleRedirectPromise();
    } catch (error) {
      console.error('B2C redirect handling failed:', error);
      redirectError =
        error instanceof Error
          ? error.message
          : 'Sign-in did not complete. Please try again.';
    }

    const accounts = msalInstance.getAllAccounts();
    if (!msalInstance.getActiveAccount() && accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }

    return result;
  };

/** The signed-in B2C account, if MSAL has one. */
export const getActiveAccount = (): AccountInfo | null =>
  msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

/**
 * A token pasted into a gitignored `.env.<mode>.local`, used instead of signing
 * in. Useful for pointing the UI at a proxy environment before its reply URL is
 * registered, or for reproducing a role-specific 403.
 *
 * Gated on the `IS_DEV_SERVER_FLAG` literal so the branch — and any value that
 * ever gets configured — folds out of every build.
 */
const getStaticAuthToken = (): string =>
  IS_DEV_SERVER_FLAG ? (import.meta.env.VITE_API_AUTH_TOKEN ?? '').trim() : '';

/** The raw B2C id token, refreshed silently when it has expired. */
const acquireIdToken = async (): Promise<string | null> => {
  const account = getActiveAccount();
  if (!account) {
    return null;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    return result.idToken;
  } catch (error) {
    // The refresh token is gone, or the policy wants the user again — a full
    // redirect is the only way forward from here.
    if (error instanceof InteractionRequiredAuthError) {
      console.warn('Silent token refresh needs interaction; redirecting.');
      void msalInstance.acquireTokenRedirect({ ...loginRequest, account });
      return null;
    }

    console.error('Failed to acquire B2C token:', error);
    return null;
  }
};

/**
 * The complete `Authorization` header value for API calls.
 *
 * The scheme is included here rather than added in `APIService` because a
 * statically configured token is passed through verbatim - a developer pasting
 * one may already have the scheme on it.
 *
 * Returns `null` rather than throwing when there is no usable token, so a failed
 * refresh degrades to an unauthenticated call — answered with 401, which
 * `APIService` turns into a bounce back to sign-in — instead of crashing a page
 * mid-render.
 */
export const acquireApiToken = async (): Promise<string | null> => {
  const staticToken = getStaticAuthToken();
  if (staticToken) {
    return staticToken;
  }

  if (DEV_BYPASS_AUTH) {
    return null;
  }

  const idToken = await acquireIdToken();
  return idToken ? `Bearer ${idToken}` : null;
};

export type SessionStatus = 'signedIn' | 'signedOut' | 'error';

export interface SessionResult {
  status: SessionStatus;
  /** User-facing message, set only when `status` is `'error'`. */
  error?: string;
  /**
   * Whether the account may use the app at all, per the app-access group
   * gate. `true` whenever the gate is disabled (see `isAppAccessGateEnabled`).
   */
  hasAppAccessGroup: boolean;
}

/**
 * Resolves whether this browser has a usable session.
 *
 * Driven by the active account rather than a redirect payload, so it also covers
 * a plain page reload, where there is no redirect response to inspect. Acquiring
 * the token here rather than lazily on the first API call means a stale cached
 * account surfaces as a sign-in error instead of a page full of empty grids.
 */
export const establishSession = async (): Promise<SessionResult> => {
  // The bypass has no token to check the group claim against, so it grants
  // access outright — it only exists on the dev server, where fixtures are
  // the point.
  if (DEV_BYPASS_AUTH) {
    return { status: 'signedIn', hasAppAccessGroup: true };
  }

  // A pasted token stands in for the whole sign-in.
  const staticToken = getStaticAuthToken();
  if (staticToken) {
    const bareToken = staticToken.replace(/^Bearer\s+/i, '');
    return {
      status: 'signedIn',
      hasAppAccessGroup: hasAppAccessGroup(bareToken),
    };
  }

  const account = getActiveAccount();
  if (!account) {
    // A failed redirect (see `redirectError`) also lands here, since it never
    // produces an account. Surfacing it beats silently re-showing the plain
    // login screen, which reads as the click having done nothing at all.
    return redirectError
      ? { status: 'error', error: redirectError, hasAppAccessGroup: false }
      : { status: 'signedOut', hasAppAccessGroup: false };
  }

  const idToken = await acquireIdToken();
  if (!idToken) {
    return {
      status: 'error',
      error:
        'Signed in, but no valid access token could be obtained. Please sign in again.',
      hasAppAccessGroup: false,
    };
  }

  // The group gate only ever looks at the B2C id token itself. DataNovaProxy
  // checks the same group server-side, so this decides what the UI shows, not
  // what the API will actually serve.
  const appAccessGroupGranted = hasAppAccessGroup(idToken);

  return { status: 'signedIn', hasAppAccessGroup: appAccessGroupGranted };
};
